import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Save } from "lucide-react";

export const FraisZonesEditor = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [zones, setZones] = useState([]);
  const [defaut, setDefaut] = useState(25);
  const [modeFraisDefaut, setModeFraisDefaut] = useState("zones");
  const [campagneActive, setCampagneActive] = useState(false);
  const [campagneDateFin, setCampagneDateFin] = useState("");
  const [saving, setSaving] = useState(false);
  const [testCp, setTestCp] = useState("");
  const [testPays, setTestPays] = useState("Belgique");
  const [testResult, setTestResult] = useState(null);

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API}/frais-zones`, { headers });
      setZones((res.data.zones || []).map((z) => ({ ...z, codes: (z.codes || []).join(", ") })));
      setDefaut(res.data.defaut ?? 25);
      setModeFraisDefaut(res.data.mode_frais_defaut || "zones");
      setCampagneActive(!!res.data.campagne_offerte_active);
      setCampagneDateFin(res.data.campagne_offerte_date_fin || "");
    } catch { toast.error("Erreur chargement des zones"); }
  };
  useEffect(() => { fetchZones(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (i, field, value) => {
    const rows = [...zones];
    rows[i] = { ...rows[i], [field]: field === "frais" ? value : value };
    setZones(rows);
  };

  const addRow = () => setZones([...zones, { label: "", pays: "Belgique", codes: "", frais: 25 }]);
  const removeRow = (i) => setZones(zones.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        zones: zones.map((z) => ({
          label: z.label,
          pays: z.pays,
          codes: String(z.codes || "").split(",").map((c) => c.trim()).filter(Boolean),
          frais: parseFloat(z.frais) || 0,
        })),
        defaut: parseFloat(defaut) || 0,
        mode_frais_defaut: modeFraisDefaut,
        campagne_offerte_active: campagneActive,
        campagne_offerte_date_fin: campagneDateFin || "",
      };
      await axios.put(`${API}/frais-zones`, payload, { headers });
      toast.success("Grille de frais enregistrée");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const testZone = async () => {
    try {
      const res = await axios.get(`${API}/frais-livraison/suggest`, { params: { code_postal: testCp, pays: testPays }, headers });
      setTestResult(res.data);
    } catch { toast.error("Erreur test"); }
  };

  return (
    <Card data-testid="frais-zones-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="w-5 h-5 text-indigo-600" /> Frais de livraison par zone
        </CardTitle>
        <CardDescription>
          Appliqués automatiquement à l'import selon le code postal du client (dépôt : Anderlecht). Codes = liste séparée par virgules, plages avec « - » (ex : 1000-1299). Premier match retenu (du haut vers le bas).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode par défaut pour les nouvelles livraisons */}
        <div className="p-3 rounded-lg border-2 border-indigo-200 bg-indigo-50" data-testid="mode-frais-defaut-block">
          <Label className="text-sm font-semibold text-indigo-900 mb-2 block">Mode de facturation par défaut (nouvelles livraisons)</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { v: "zones", label: "🗺️ Prix par zones", desc: "Utilise la grille ci-dessous" },
              { v: "forfait", label: `💵 Forfait ${defaut}€`, desc: "Prix unique pour tous" },
              { v: "offerte", label: "🎁 Livraison offerte", desc: "0€ sur toute nouvelle livraison" },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setModeFraisDefaut(opt.v)}
                className={`text-left p-3 rounded-lg border-2 transition ${modeFraisDefaut === opt.v ? "bg-indigo-600 text-white border-indigo-700 shadow" : "bg-white border-slate-200 hover:border-indigo-300"}`}
                data-testid={`mode-defaut-${opt.v}`}
              >
                <div className="font-semibold text-sm">{opt.label}</div>
                <div className={`text-xs mt-1 ${modeFraisDefaut === opt.v ? "text-indigo-100" : "text-slate-500"}`}>{opt.desc}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-2">
            📌 Ce mode s&apos;applique à toutes les <strong>nouvelles</strong> livraisons créées. Les livraisons existantes gardent leur prix, et tu peux toujours changer le mode d&apos;une tournée entière depuis son détail.
          </p>
        </div>

        {/* Campagne livraison offerte avec date */}
        <div className={`p-3 rounded-lg border-2 ${campagneActive ? "bg-rose-50 border-rose-300" : "bg-slate-50 border-slate-200"}`} data-testid="campagne-offerte-block">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={campagneActive} onChange={(e) => setCampagneActive(e.target.checked)} data-testid="campagne-offerte-toggle" />
                <div className="w-11 h-6 bg-slate-300 peer-checked:bg-rose-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
              <div>
                <div className="font-semibold text-sm flex items-center gap-2">
                  🎁 Campagne « Livraison offerte » temporaire {campagneActive && <span className="text-rose-600 font-bold text-xs px-2 py-0.5 bg-rose-100 rounded-full">ACTIVE</span>}
                </div>
                <p className="text-xs text-slate-600 mt-0.5">
                  {campagneActive
                    ? "Force le mode « Livraison offerte » sur toutes les nouvelles livraisons, même si le mode par défaut est différent."
                    : "Utile pour une promo temporaire. S'auto-désactive à la date de fin."}
                </p>
              </div>
            </div>
            {campagneActive && (
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Fin (optionnelle) :</Label>
                <Input type="date" value={campagneDateFin} onChange={(e) => setCampagneDateFin(e.target.value)} className="h-9 w-40" data-testid="campagne-date-fin-input" />
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[160px]">Zone</TableHead>
                <TableHead className="w-32">Pays</TableHead>
                <TableHead className="min-w-[200px]">Codes postaux / plages</TableHead>
                <TableHead className="w-24 text-right">Frais €</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.map((z, i) => (
                <TableRow key={i} data-testid={`zone-row-${i}`}>
                  <TableCell><Input value={z.label} onChange={(e) => updateRow(i, "label", e.target.value)} className="h-9" data-testid={`zone-label-${i}`} /></TableCell>
                  <TableCell><Input value={z.pays} onChange={(e) => updateRow(i, "pays", e.target.value)} className="h-9" data-testid={`zone-pays-${i}`} /></TableCell>
                  <TableCell><Input value={z.codes} onChange={(e) => updateRow(i, "codes", e.target.value)} placeholder="1000-1299, 1700" className="h-9" data-testid={`zone-codes-${i}`} /></TableCell>
                  <TableCell><Input type="number" step="0.01" value={z.frais} onChange={(e) => updateRow(i, "frais", e.target.value)} className="h-9 text-right" data-testid={`zone-frais-${i}`} /></TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => removeRow(i)} data-testid={`zone-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="zone-add-btn"><Plus className="w-4 h-4 mr-1" />Ajouter une zone</Button>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Frais par défaut (zone inconnue)</Label>
              <Input type="number" step="0.01" value={defaut} onChange={(e) => setDefaut(e.target.value)} className="h-9 w-32" data-testid="zone-defaut-input" />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 ml-auto" data-testid="zone-save-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? "Enregistrement…" : "Enregistrer la grille"}
          </Button>
        </div>

        <div className="border-t pt-4 flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Tester un code postal</Label>
            <Input value={testCp} onChange={(e) => setTestCp(e.target.value)} placeholder="59000" className="h-9 w-32" data-testid="zone-test-cp" />
          </div>
          <div>
            <Label className="text-xs">Pays</Label>
            <Input value={testPays} onChange={(e) => setTestPays(e.target.value)} className="h-9 w-32" data-testid="zone-test-pays" />
          </div>
          <Button variant="outline" size="sm" onClick={testZone} data-testid="zone-test-btn">Tester</Button>
          {testResult && (
            <span className="text-sm font-medium text-indigo-700" data-testid="zone-test-result">→ {testResult.zone} : {Number(testResult.frais).toFixed(2)} €</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FraisZonesEditor;
